import React, { useState, useEffect } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

const UserTreningi = () => {
  const [treningi, setTreningi] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMessage, setShowMessage] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const itemsPerPage = 5; // Number of treningi per page
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchTreningi = async () => {
      if (!token) {
        setError("No token found. Please log in.");
        setLoading(false);
        return;
      }

      try {
        const decodedToken = jwtDecode(token);
        const userId = decodedToken._id;

        const response = await axios.get(
          `${BACKEND_URL}/api/treningi/getTreningiByUserId/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        // Sort treningi by date (earliest first)
        const sortedTreningi = response.data.sort(
          (a, b) => new Date(b.godzina_start) - new Date(a.godzina_start)
        );
        setTreningi(sortedTreningi);
      } catch (err) {
        setError(
          err.response?.data?.message || "Failed to fetch training sessions."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTreningi();
  }, [token]);

  const totalPages = Math.ceil(treningi.length / itemsPerPage);
  const currentItems = treningi.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (direction) => {
    if (direction === "next" && currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    } else if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleCancelTraining = async (trening) => {
    const now = new Date();
    const utcStartTime = new Date(trening.godzina_start);
    const timeDifference = utcStartTime - now;
   
    if (timeDifference > 24 * 60 * 60 * 1000) {
      try {
        // Update training status
        await axios.put(
          `${BACKEND_URL}/api/treningi/updateTrening/${trening._id}`,
          { status: "anulowany" },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // Update karnet entries if exists
        if (trening.karnet_id) {
          await axios.put(
            `${BACKEND_URL}/api/karnet/updateKarnet/${trening.karnet_id._id}`,
            {
              ilosc_wejsc_kal: trening.karnet_id.ilosc_wejsc_kal + 1,
            },{
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
        }

        // Update local state
        setTreningi(treningi.filter((t) => t._id !== trening._id));
        setShowMessage(true);

        // Hide message after 5 seconds
        setTimeout(() => {
          setShowMessage(false);
        }, 5000);
      } catch (error) {
        console.error("Error canceling training:", error);
        alert("Wystąpił błąd podczas anulowania treningu.");
      }
    } else {
      alert(
        "Nie możesz anulować treningu na mniej niż 24 godziny przed rozpoczęciem."
      );
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Historia Treningów</h1>

      {showMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          <p>Nie trać motywacji i umów się na kolejny trening!</p>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <ul>
          {currentItems.map((trening) => (
            <li
              key={trening._id}
              className="border-b last:border-none p-4 flex justify-between items-center hover:bg-gray-50"
            >
              <div>
                <h2 className="text-lg font-semibold">
                  {trening.typ_treningu}
                </h2>
                <p className="text-sm text-gray-600">
                  <strong>Data:</strong>{" "}
                  {new Date(trening.data).toLocaleDateString("pl-PL")}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Godzina:</strong>{" "}
                  {new Date(trening.godzina_start).toLocaleTimeString("pl-PL", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  })}{" "}
                  -{" "}
                  {new Date(trening.godzina_koniec).toLocaleTimeString(
                    "pl-PL",
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    }
                  )}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Status:</strong> {trening.status}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                  #{trening._id.slice(-4)}
                </span>
                {trening.status !== "anulowany" && (
                  <button
                    onClick={() => handleCancelTraining(trening)}
                    className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg text-sm transition-colors"
                  >
                    Anuluj
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex justify-between items-center mt-4">
        <button
          onClick={() => handlePageChange("prev")}
          disabled={currentPage === 1}
          className={`px-4 py-2 rounded-lg ${
            currentPage === 1
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-blue-500 text-white hover:bg-blue-600"
          }`}
        >
          Poprzednia
        </button>
        <p className="text-sm text-gray-600">
          Strona {currentPage} z {totalPages}
        </p>
        <button
          onClick={() => handlePageChange("next")}
          disabled={currentPage === totalPages}
          className={`px-4 py-2 rounded-lg ${
            currentPage === totalPages
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-blue-500 text-white hover:bg-blue-600"
          }`}
        >
          Następna
        </button>
      </div>
    </div>
  );
};

export default UserTreningi;
